/**
 * AdminColaA6.jsx · Agente A6 — Cola priorizada con control manual.
 */
import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Target, RefreshCw, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown,
  Lock, Unlock, Loader2, TrendingUp, Users, Clock, AlertOctagon,
} from "lucide-react";

export default function AdminColaA6() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/a6/queue?limit=200");
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (e) { toast.error("Error cargando cola"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const recompute = async () => {
    try {
      await api.post("/a6/recompute");
      toast.success("Cola recomputada");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const move = async (id, direction) => {
    setBusyId(id);
    try {
      await api.post(`/a6/move/${id}`, { direction });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusyId(null); }
  };

  const unlock = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/a6/unlock/${id}`);
      toast.success("OT liberada · vuelve a movimiento automático");
      await load();
    } catch (e) { toast.error("Error"); }
    finally { setBusyId(null); }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="a6-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#EDE9FE", border: "1px solid #A78BFA" }}>
              <Target size={11} className="text-purple-700" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-purple-700">Agente A6 · Priorización dinámica</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Cola de despacho</h1>
            <p className="mt-1 text-sm text-gray-500">{total} OTs · Score = SLA(40%) + Cliente(25%) + Solución(20%) + Espera(15%). Mueve manualmente para bloquear la posición.</p>
          </div>
          <button onClick={recompute} className="neu-btn neu-btn-primary" data-testid="btn-recompute">
            <RefreshCw size={14}/> Recomputar
          </button>
        </div>

        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gray-400"/></div>
        : (
          <div className="neu-card overflow-hidden">
            <table className="w-full text-xs">
              <thead className="text-[9px] uppercase tracking-widest text-gray-500 border-b bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-center w-12">#</th>
                  <th className="px-2 py-2 text-center w-16">Score</th>
                  <th className="px-2 py-2 text-left">OT / Cliente</th>
                  <th className="px-2 py-2 text-left">Tipo</th>
                  <th className="px-2 py-2 text-center">Factores</th>
                  <th className="px-2 py-2 text-center w-32">Mover</th>
                  <th className="px-2 py-2 text-center w-16">Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.odoo_task_id} className={`border-b hover:bg-gray-50 ${it.locked ? "bg-yellow-50" : ""}`}
                      data-testid={`a6-row-${it.odoo_task_id}`}>
                    <td className="px-2 py-2 text-center font-bold tabular-nums" style={{ color: "#0A3747" }}>{it.position}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full font-bold tabular-nums ${
                        it.score >= 80 ? "bg-red-100 text-red-800" :
                        it.score >= 60 ? "bg-amber-100 text-amber-800" :
                        "bg-emerald-100 text-emerald-800"
                      }`}>{Number(it.score).toFixed(1)}</span>
                    </td>
                    <td className="px-2 py-2">
                      <button onClick={() => navigate(`/ot-smart/${it.odoo_task_id}`)}
                              className="font-semibold text-[#0A3747] hover:underline text-left"
                              data-testid={`a6-goto-${it.odoo_task_id}`}>
                        {it.task_number || `#${it.odoo_task_id}`}
                      </button>
                      <div className="text-[10px] text-gray-500 truncate max-w-[240px]">{it.client_name}</div>
                    </td>
                    <td className="px-2 py-2 text-xs">
                      <div>{it.activity_type || "—"}</div>
                      <div className="text-[10px] text-gray-500">{it.segment_name || it.plan_type || "—"}</div>
                    </td>
                    <td className="px-2 py-2 text-[10px]">
                      <div className="flex items-center justify-center gap-2 tabular-nums">
                        <span title="SLA" className="flex items-center gap-0.5"><Clock size={9}/>{it.factors?.sla?.value?.toFixed(0)}</span>
                        <span title="Cliente" className="flex items-center gap-0.5"><Users size={9}/>{it.factors?.client?.value}</span>
                        <span title="Solución" className="flex items-center gap-0.5"><AlertOctagon size={9}/>{it.factors?.solution?.value}</span>
                        <span title="Espera" className="flex items-center gap-0.5"><TrendingUp size={9}/>{it.factors?.wait?.value?.toFixed(0)}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-center gap-0.5">
                        {busyId === it.odoo_task_id ? <Loader2 size={12} className="animate-spin"/> : (
                          <>
                            <IconBtn onClick={() => move(it.odoo_task_id, "top")} icon={ChevronsUp} title="Al tope" testid={`move-top-${it.odoo_task_id}`}/>
                            <IconBtn onClick={() => move(it.odoo_task_id, "up")} icon={ChevronUp} title="Arriba" testid={`move-up-${it.odoo_task_id}`}/>
                            <IconBtn onClick={() => move(it.odoo_task_id, "down")} icon={ChevronDown} title="Abajo" testid={`move-down-${it.odoo_task_id}`}/>
                            <IconBtn onClick={() => move(it.odoo_task_id, "bottom")} icon={ChevronsDown} title="Al final" testid={`move-bot-${it.odoo_task_id}`}/>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {it.locked ? (
                        <button onClick={() => unlock(it.odoo_task_id)}
                                title={`Bloqueado por ${it.locked_by}`}
                                className="text-yellow-700"
                                data-testid={`unlock-${it.odoo_task_id}`}>
                          <Lock size={13}/>
                        </button>
                      ) : (
                        <Unlock size={13} className="text-gray-300 mx-auto"/>
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan="7" className="py-10 text-center text-gray-500">Cola vacía. Corre &quot;Recomputar&quot;.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

function IconBtn({ onClick, icon: Icon, title, testid }) {
  return (
    <button onClick={onClick} title={title} className="p-1 hover:bg-gray-200 rounded" data-testid={testid}>
      <Icon size={12}/>
    </button>
  );
}
